<template>
  <div class="p-1.5 md:p-3 bg-gray-50 dark:bg-gray-900 min-h-screen font-sans">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
      <div class="flex items-center space-x-3">
        <div class="h-8 w-1 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full"></div>
        <div>
          <h1 class="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">Recepción y Reparación</h1>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 flex items-center mt-0.5">
            <Icon icon="mdi:flash-outline" class="mr-1" />
            Jean Informatico - Taller de Servicio Técnico
          </p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <!-- 🔥 INDICADOR DE ACTUALIZACIÓN -->
        <span class="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
          <Icon :icon="pollingActive ? 'mdi:clock' : 'mdi:clock-outline'" class="text-xs" />
          {{ pollingActive ? 'Actualizando...' : 'Auto-actualización' }}
        </span>
        <button @click="resetForm" class="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow flex items-center gap-2 text-xs font-medium">
          <Icon icon="mdi:refresh" class="text-base" />
          Limpiar Formulario
        </button>
      </div>
    </div>

    <!-- Formulario en dos columnas -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <!-- Columna izquierda -->
      <div class="space-y-3">
        <!-- Tarjeta de Cliente -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg mr-2">
                <Icon icon="mdi:account" class="text-green-600 dark:text-green-400 text-lg" />
              </div>
              Información del Cliente
              <span class="ml-2 text-[10px] bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded-full">* Requerido</span>
            </h2>
          </div>
          <div class="p-3">
            <!-- Pestañas -->
            <div class="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-0.5 rounded-xl mb-3">
              <button @click="modoCliente = 'buscar'" :class="['flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200', modoCliente === 'buscar' ? 'bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200']">
                <Icon icon="mdi:magnify" class="inline mr-1" /> Buscar
              </button>
              <button @click="modoCliente = 'crear'" :class="['flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200', modoCliente === 'crear' ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200']">
                <Icon icon="mdi:account-plus" class="inline mr-1" /> Nuevo
              </button>
            </div>

            <!-- Modo Buscar Cliente -->
            <div v-if="modoCliente === 'buscar'" class="relative">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Buscar Cliente Existente</label>
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="buscarCliente" type="text" placeholder="Nombre, documento, teléfono o email..." class="w-full pl-10 pr-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-green-500 text-sm transition-all" @input="debounceBuscarClientes" @focus="mostrarClientesSiHay" />
              </div>
              
              <!-- Dropdown de resultados -->
              <div v-if="mostrarListaClientes && clientesEncontrados.length > 0" class="absolute mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-72 overflow-y-auto z-50">
                <div v-for="(cliente, index) in clientesEncontrados" :key="cliente.idCliente || cliente.id || index" @click="seleccionarCliente(cliente)" class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div class="flex items-start gap-3">
                    <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <Icon icon="mdi:account-circle" class="text-green-600 dark:text-green-400 text-xl" />
                    </div>
                    <div class="flex-1">
                      <div class="flex justify-between items-start">
                        <div>
                          <p class="font-semibold text-gray-800 dark:text-white">{{ cliente.nombre }} {{ cliente.apellido }}</p>
                          <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{{ formatearTipoDocumento(cliente.tipoDocumento) }}: <span class="font-mono">{{ cliente.documento }}</span></p>
                          <div class="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span v-if="cliente.telefono" class="flex items-center"><Icon icon="mdi:phone" class="mr-1 text-xs" />{{ cliente.telefono }}</span>
                          </div>
                        </div>
                        <Icon v-if="form.idCliente === cliente.idCliente" icon="mdi:check-circle" class="text-green-500 text-2xl" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="buscarCliente && buscarCliente.trim().length >= 2 && !cargandoClientes && clientesEncontrados.length === 0" class="absolute mt-2 w-full p-6 text-center bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 z-50">
                <Icon icon="mdi:account-search" class="text-4xl text-gray-400 mx-auto mb-2" />
                <p class="text-gray-600 dark:text-gray-400 mb-3">No encontramos "{{ buscarCliente }}"</p>
                <button @click="modoCliente = 'crear'" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2">
                  <Icon icon="mdi:account-plus" />Crear este cliente
                </button>
              </div>
              
              <div v-if="cargandoClientes" class="absolute mt-2 w-full p-4 text-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xl z-50">
                <div class="inline-block animate-spin rounded-full h-5 w-5 border-2 border-green-500 border-t-transparent"></div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">Buscando clientes...</p>
              </div>
            </div>

            <!-- Modo Crear Cliente -->
            <div v-if="modoCliente === 'crear'">
              <form @submit.prevent="guardarNuevoCliente" class="space-y-4">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Documento <span class="text-red-500">*</span></label>
                    <select v-model="nuevoCliente.tipoDocumento" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all">
                      <option value="">Seleccionar...</option>
                      <option value="cedula">Cédula de Identidad</option>
                      <option value="rif">RIF</option>
                      <option value="pasaporte">Pasaporte</option>
                      <option value="identificacion_extranjera">Identificación Extranjera</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Número Documento <span class="text-red-500">*</span></label>
                    <input v-model="nuevoCliente.documento" type="text" :placeholder="getPlaceholderDocumento(nuevoCliente.tipoDocumento)" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="formatearDocumentoInput" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre <span class="text-red-500">*</span></label>
                    <input v-model="nuevoCliente.nombre" type="text" placeholder="Ej: Juan Carlos" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="nuevoCliente.nombre = toTitleCase(nuevoCliente.nombre)" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido</label>
                    <input v-model="nuevoCliente.apellido" type="text" placeholder="Ej: Rodríguez" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="nuevoCliente.apellido = toTitleCase(nuevoCliente.apellido)" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                    <input v-model="nuevoCliente.telefono" type="text" placeholder="0414-7470715" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @blur="formatearTelefonoInput" />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input v-model="nuevoCliente.email" type="email" placeholder="ejemplo@email.com" class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" />
                  </div>
                  <div class="col-span-2">
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección <span class="text-red-500">*</span></label>
                    <textarea v-model="nuevoCliente.direccion" rows="2" placeholder="Dirección completa del cliente..." class="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all resize-none" @blur="nuevoCliente.direccion = toTitleCase(nuevoCliente.direccion)"></textarea>
                  </div>
                </div>
                <div class="flex gap-3 pt-2">
                  <button type="submit" :disabled="!puedeGuardarCliente || guardandoCliente" :class="['flex-1 px-4 py-2 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 font-medium', puedeGuardarCliente && !guardandoCliente ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed']">
                    <Icon v-if="guardandoCliente" icon="mdi:loading" class="animate-spin text-lg" />
                    <Icon v-else icon="mdi:account-plus" class="text-lg" />
                    {{ guardandoCliente ? 'Guardando...' : 'Guardar y Seleccionar' }}
                  </button>
                  <button type="button" @click="modoCliente = 'buscar'" class="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200">Cancelar</button>
                </div>
              </form>
            </div>
            
            <!-- Cliente Seleccionado -->
            <div v-if="clienteSeleccionado" class="mt-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 rounded-xl border border-green-200 dark:border-green-800/50">
              <div class="flex items-start gap-3">
                <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Icon icon="mdi:account-check" class="text-green-600 dark:text-green-400 text-2xl" />
                </div>
                <div class="flex-1">
                  <div class="flex justify-between items-start">
                    <div>
                      <p class="font-semibold text-gray-800 dark:text-white text-lg">{{ clienteSeleccionado.nombre }} {{ clienteSeleccionado.apellido }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{{ formatearTipoDocumento(clienteSeleccionado.tipoDocumento) }}: <span class="font-mono">{{ clienteSeleccionado.documento }}</span></p>
                      <div class="flex flex-wrap gap-3 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <span v-if="clienteSeleccionado.telefono" class="flex items-center"><Icon icon="mdi:phone" class="mr-1 text-gray-400" />{{ clienteSeleccionado.telefono }}</span>
                      </div>
                    </div>
                    <button @click="deseleccionarCliente" class="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200" title="Cambiar cliente">
                      <Icon icon="mdi:close" class="text-xl" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Información de la Recepción -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-2">
                <Icon icon="mdi:file-document-edit" class="text-blue-600 dark:text-blue-400 text-lg" />
              </div>
              Detalles de la Orden
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">N° Orden</label>
                <div class="relative">
                  <Icon icon="mdi:tag" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input :value="numeroOrdenDisplay" type="text" readonly class="w-full pl-9 pr-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 cursor-not-allowed font-mono text-xs" />
                </div>
              </div>
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Sede / Taller <span class="text-red-500">*</span></label>
                <div class="relative">
                  <Icon icon="mdi:store-marker" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <select v-model="form.idSede" @change="actualizarNombreTaller" class="w-full pl-9 pr-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-500 text-xs transition-all">
                    <option value="null">-- Sede --</option>
                    <option v-for="sede in sedesList" :key="sede.idSede" :value="sede.idSede">{{ sede.nombre }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Código Producto / Equipo <span class="text-red-500">*</span></label>
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="buscarProducto" @keyup.f2="mostrarModalF2Productos" type="text" placeholder="Código o nombre... (F2 lista)" class="w-full pl-10 pr-12 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm transition-all" @input="debounceBuscarProductos" @focus="mostrarProductosSiHay" />
                <button @click="mostrarModalF2Productos" title="Ver Lista Completa (F2)" class="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded shadow-sm text-[10px] border border-blue-200 hover:bg-blue-200 font-bold transition-colors">LISTA F2</button>
                
                <div v-if="mostrarListaProductos && productosEncontrados.length > 0" class="absolute mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-60 overflow-y-auto z-50">
                  <div v-for="ps in productosEncontrados" :key="ps.idProductoServicio" @click="seleccionarProducto(ps)" class="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                    <div class="flex justify-between items-center text-sm">
                      <div>
                        <p class="font-bold text-gray-800 dark:text-white">{{ ps.codigo }}</p>
                        <p class="text-[11px] text-gray-600 dark:text-gray-400">{{ ps.nombre }}</p>
                      </div>
                      <Icon v-if="form.idProductoServicios === ps.idProductoServicio" icon="mdi:check-circle" class="text-green-500" />
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="productoSeleccionado" class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50 relative">
                <div class="flex items-center gap-2">
                  <div class="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Icon icon="mdi:barcode-scan" class="text-blue-600 dark:text-blue-400 text-base" />
                  </div>
                  <div class="flex-1 overflow-hidden">
                    <p class="text-xs font-bold text-gray-800 dark:text-white truncate">{{ productoSeleccionado.codigo }} - {{ productoSeleccionado.nombre }}</p>
                  </div>
                  <button @click="deseleccionarProducto" class="text-gray-400 hover:text-red-500 transition-colors">
                    <Icon icon="mdi:close-circle" class="text-lg" />
                  </button>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Descripción <span class="text-red-500">*</span></label>
                <div class="relative">
                  <Icon icon="mdi:text-box-edit-outline" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input v-model="form.descripcion" type="text" placeholder="Equipo o servicio..." class="w-full pl-9 pr-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-500 text-xs transition-all" @blur="form.descripcion = toTitleCase(form.descripcion)" />
                </div>
              </div>
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Diagnóstico <span class="text-red-500">*</span></label>
                <div class="relative">
                  <Icon icon="mdi:stethoscope" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <select v-model="form.tipoDiagnostico" @change="actualizarMontoDiagnostico" class="w-full pl-9 pr-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-500 text-xs font-bold transition-all">
                    <option value="GRATIS">GRATIS</option>
                    <option value="EXPRESS">EXPRESS (10$)</option>
                    <option value="ADELANTADO">PAGO ADELANTADO</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tarjeta de Servicio y Cobro Inicial (Anticipo / Total) - Solo si PAGO ADELANTADO -->
        <div v-if="form.tipoDiagnostico === 'ADELANTADO'" class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeIn">
          <div class="bg-gradient-to-r from-emerald-500 to-teal-600 px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center text-white">
            <h2 class="text-sm font-black flex items-center gap-2">
              <div class="p-1.5 bg-white/20 rounded-lg">
                <Icon icon="mdi:cash-register" class="text-white text-base" />
              </div>
              Servicio / Reparación y Cobro Inicial
            </h2>
            <div class="text-[9px] font-black px-2.5 py-1 bg-white/20 rounded-lg backdrop-blur-sm tracking-widest uppercase">
              Total: ${{ subtotalItemsAgregados.toFixed(2) }}
            </div>
          </div>

          <div class="p-3 space-y-3">
            <!-- Botones Catálogo (Igual a SeguimientoOrden Fase 2) -->
            <div>
              <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Asignar al Cliente (Servicios y Productos) <span class="text-red-500">*</span>
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button type="button" @click="abrirModalConfiguracionItem('servicio')" class="py-2.5 px-3 rounded-xl transition-all flex items-center justify-center gap-2 text-xs font-black uppercase bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500 text-emerald-700 dark:text-emerald-300 shadow-sm hover:bg-emerald-100 dark:hover:bg-emerald-900/40 active:scale-95">
                  <Icon icon="mdi:wrench" class="text-base" /> + AGREGAR SERVICIO
                </button>
                <button type="button" @click="abrirModalConfiguracionItem('producto')" class="py-2.5 px-3 rounded-xl transition-all flex items-center justify-center gap-2 text-xs font-black uppercase bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 text-blue-700 dark:text-blue-300 shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 active:scale-95">
                  <Icon icon="mdi:package-variant" class="text-base" /> + AGREGAR PRODUCTO
                </button>
              </div>
            </div>

            <!-- Repuesto o Servicio Manual (como en Fase 2) -->
            <div class="p-2.5 bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-700">
              <label class="block text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Icon icon="mdi:plus-box-outline" class="text-emerald-600" /> Repuesto o Servicio Manual
              </label>
              <div class="flex gap-2">
                <input 
                  v-model="nombreItemManual" 
                  type="text" 
                  placeholder="Ej: Cambio de conector de carga manual..." 
                  class="flex-1 px-3 py-1.5 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-xs font-bold outline-none focus:border-emerald-500 transition-all" 
                />
                <div class="w-24 relative">
                  <span class="absolute left-2 top-1/2 -translate-y-1/2 font-black text-emerald-600 text-xs">$</span>
                  <input 
                    v-model.number="precioItemManual" 
                    type="number" 
                    step="0.01" 
                    min="0"
                    placeholder="0.00" 
                    class="w-full pl-5 pr-2 py-1.5 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-xs font-black text-emerald-600 outline-none focus:border-emerald-500 text-right transition-all" 
                  />
                </div>
                <button 
                  type="button"
                  @click="agregarItemManual" 
                  class="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow font-black flex items-center justify-center transition-all active:scale-95"
                >
                  <Icon icon="mdi:plus-thick" class="text-base" />
                </button>
              </div>
            </div>

            <!-- Lista de Ítems Agregados (Igual a Fase 2) -->
            <div class="space-y-1.5">
              <div class="flex justify-between items-center px-1">
                <span class="text-[9px] font-black text-gray-400 uppercase tracking-wider">Ítems Asignados ({{ itemsAgregados.length }})</span>
                <span class="text-[10px] font-black text-emerald-600 dark:text-emerald-400">Subtotal: ${{ subtotalItemsAgregados.toFixed(2) }}</span>
              </div>

              <div v-if="itemsAgregados.length === 0" class="p-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-center">
                <Icon icon="mdi:cart-plus" class="text-2xl text-gray-400 mx-auto mb-1" />
                <p class="text-[11px] font-bold text-gray-400">No hay servicios ni productos agregados todavía.</p>
                <p class="text-[9px] text-gray-400">Haga clic en <b>+ AGREGAR SERVICIO</b> o <b>+ AGREGAR PRODUCTO</b> para asignar al cliente.</p>
              </div>

              <div v-else class="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                <div v-for="it in itemsAgregados" :key="it.uuid" class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm animate-fadeIn">
                  <div class="flex-1 overflow-hidden mr-2">
                    <div class="flex items-center gap-1.5 flex-wrap">
                      <div class="p-1 rounded-md" :class="it.tipo === 'servicio' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'">
                        <Icon :icon="it.tipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xs" />
                      </div>
                      <input v-model="it.nombre" class="flex-1 min-w-[120px] text-[10px] font-bold text-gray-800 dark:text-white uppercase bg-transparent border-b border-transparent focus:border-emerald-500 outline-none" />
                      <span v-if="it.cantidad && it.cantidad > 1" class="text-[8px] font-black bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">x{{ it.cantidad }}</span>
                      <span v-if="it.garantia && it.garantia > 0" class="text-[8px] font-black bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        🛡️ {{ formatearGarantia(it.garantia) }}
                      </span>
                    </div>
                    <p class="text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                      {{ it.tipo.toUpperCase() }} {{ it.cantidad && it.cantidad > 1 ? `($${Number(it.precio_unitario || 0).toFixed(2)} c/u)` : '' }}
                    </p>
                  </div>

                  <div class="flex items-center gap-2 shrink-0">
                    <div class="flex items-center bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded-lg border border-gray-200 dark:border-gray-700">
                      <span class="text-[9px] text-gray-400 mr-1">$</span>
                      <input 
                        v-model.number="it.precio_total" 
                        @input="actualizarDesdePrecioTotalItem(it)" 
                        type="number" 
                        step="0.01" 
                        class="w-16 text-[11px] font-black bg-transparent text-right outline-none text-emerald-600 dark:text-emerald-400" 
                      />
                    </div>
                    <button type="button" @click="eliminarItemAgregado(it.uuid)" class="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                      <Icon icon="mdi:trash-can-outline" class="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modalidad de Cobro Adelantado (Total 100% o Abono) -->
            <div class="pt-2 border-t border-gray-100 dark:border-gray-700">
              <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                Modalidad de Pago Adelantado
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button 
                  type="button" 
                  @click="cambiarModalidadPago('TOTAL')"
                  :class="['py-2 px-3 rounded-xl border-2 text-center transition-all text-xs font-black uppercase flex items-center justify-center gap-1.5', pagoInicial.modalidad === 'TOTAL' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 shadow-sm' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-emerald-300']"
                >
                  <Icon icon="mdi:check-all" class="text-sm" /> Pago Total (100%)
                </button>
                <button 
                  type="button" 
                  @click="cambiarModalidadPago('ABONO')"
                  :class="['py-2 px-3 rounded-xl border-2 text-center transition-all text-xs font-black uppercase flex items-center justify-center gap-1.5', pagoInicial.modalidad === 'ABONO' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-sm' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-blue-300']"
                >
                  <Icon icon="mdi:cash-fast" class="text-sm" /> Abono / Anticipo Parcial
                </button>
              </div>
            </div>

            <!-- Panel de detalles de cobro adelantado -->
            <div class="p-3 bg-gradient-to-r from-emerald-50/60 to-teal-50/60 dark:from-gray-900/60 dark:to-gray-800/60 rounded-xl border border-emerald-200 dark:border-emerald-800/40 space-y-3 animate-fadeIn">
              
              <!-- Input monto de Abono si la modalidad es ABONO -->
              <div v-if="pagoInicial.modalidad === 'ABONO'" class="grid grid-cols-2 gap-3 items-center">
                <div>
                  <label class="block text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">
                    Monto a Abonar Hoy (USD) <span class="text-red-500">*</span>
                  </label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 font-black text-blue-600 text-xs">$</span>
                    <input v-model.number="pagoInicial.montoCobrar" type="number" step="0.01" min="1" class="w-full pl-7 pr-3 py-1.5 border-2 border-blue-300 dark:border-blue-600 rounded-xl bg-white dark:bg-gray-700 text-xs font-black text-blue-700 dark:text-blue-300 text-right outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                </div>
                <div class="p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-right">
                  <span class="text-[9px] font-bold text-gray-400 uppercase block">Saldo al Retirar</span>
                  <span class="text-xs font-black text-orange-600 dark:text-orange-400">${{ saldoRestanteCalculado.toFixed(2) }}</span>
                </div>
              </div>

              <!-- Resumen financiero del cobro -->
              <div class="grid grid-cols-3 gap-2 text-center bg-white dark:bg-gray-800 p-2.5 rounded-xl border border-emerald-100 dark:border-gray-700">
                <div>
                  <span class="text-[8px] font-bold text-gray-400 uppercase block">Total Servicios</span>
                  <span class="text-xs font-black text-gray-800 dark:text-white">${{ subtotalItemsAgregados.toFixed(2) }}</span>
                </div>
                <div>
                  <span class="text-[8px] font-black text-emerald-600 uppercase block">Cobro Inicial Hoy</span>
                  <span class="text-xs font-black text-emerald-600 dark:text-emerald-400">${{ montoCobrarCalculado.toFixed(2) }}</span>
                  <span class="text-[8px] text-gray-400 block font-bold">≈ Bs {{ montoCobrarBsCalculado }}</span>
                </div>
                <div>
                  <span class="text-[8px] font-bold uppercase block" :class="saldoRestanteCalculado === 0 ? 'text-emerald-600' : 'text-orange-500'">
                    {{ saldoRestanteCalculado === 0 ? 'Estado' : 'Saldo al Retirar' }}
                  </span>
                  <span class="text-xs font-black" :class="saldoRestanteCalculado === 0 ? 'text-emerald-600' : 'text-orange-600'">
                    {{ saldoRestanteCalculado === 0 ? 'PAGADO 100% ✅' : `$${saldoRestanteCalculado.toFixed(2)}` }}
                  </span>
                </div>
              </div>

              <!-- Métodos de pago -->
              <div>
                <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Método de Pago <span class="text-red-500">*</span>
                </label>
                <div class="grid grid-cols-3 sm:grid-cols-6 gap-1">
                  <button v-for="m in metodosPago" :key="m.value" type="button"
                    @click="pagoInicial.metodoPago = m.value"
                    :class="['flex flex-col items-center justify-center p-1.5 rounded-lg border text-center transition-all', pagoInicial.metodoPago === m.value ? 'border-emerald-500 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 shadow-sm font-bold' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-emerald-300 text-[10px]']">
                    <Icon :icon="m.icon" class="text-sm mb-0.5" />
                    <span class="text-[8px] leading-tight">{{ m.label }}</span>
                  </button>
                </div>
              </div>

              <!-- Referencia -->
              <div v-if="pagoInicial.metodoPago && pagoInicial.metodoPago !== 'EFECTIVO_USD'">
                <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  N° de Referencia / Comprobante
                </label>
                <input v-model="pagoInicial.referencia" type="text" placeholder="Ej: 12345678 o 4 dígitos de punto..." class="w-full px-3 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-xs font-mono outline-none focus:ring-1 focus:ring-emerald-500 transition-all" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna derecha -->
      <div class="space-y-3">
        <!-- Detalles del Equipo -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-2">
                <Icon icon="mdi:laptop" class="text-orange-600 dark:text-orange-400 text-lg" />
              </div>
              Estado del Equipo
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Problema Reportado <span class="text-red-500">*</span></label>
              <textarea v-model="form.problema" rows="4" placeholder="Fallo o motivo de ingreso..." class="w-full px-3 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-orange-500 text-sm resize-none transition-all" @blur="form.problema = toTitleCase(form.problema)"></textarea>
            </div>
            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Accesorios / Clave <span class="text-red-500">*</span></label>
              <div class="relative">
                <Icon icon="mdi:lock-pattern" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                <textarea v-model="form.accesorios" rows="3" maxlength="200" placeholder="Cargador, Cover, Clave..." class="w-full pl-9 pr-3 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-1 focus:ring-orange-500 text-sm resize-none transition-all" @input="limitarAccesorios" @blur="form.accesorios = toTitleCase(form.accesorios)"></textarea>
              </div>
              <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Máximo 200 caracteres · {{ form.accesorios.length }}/200</p>
            </div>
          </div>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-teal-100 dark:bg-teal-900/30 rounded-lg mr-2">
                <Icon icon="mdi:calendar-clock" class="text-teal-600 dark:text-teal-400 text-lg" />
              </div>
              Línea de Tiempo (Fechas)
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div class="grid grid-cols-1 gap-3">
              <div>
                <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Fecha de Entrada</label>
                <div class="relative">
                  <Icon icon="mdi:login" class="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input v-model="form.fechaIngreso" type="datetime-local" class="w-full pl-9 pr-2 py-1.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 font-mono text-xs outline-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Asignación -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-100 dark:border-gray-700">
          <div class="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-gray-800 dark:to-gray-800 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-base font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-2">
                <Icon icon="mdi:account-wrench" class="text-purple-600 dark:text-purple-400 text-lg" />
              </div>
              Asignación Técnica
            </h2>
          </div>
          <div class="p-3 space-y-3">
            <div>
              <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1 leading-none uppercase tracking-wider">Técnico que Recibe <span class="text-red-500">*</span></label>
              <div class="relative">
                <div class="relative">
                  <Icon icon="mdi:account-hard-hat" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500" />
                  <input 
                    :value="tecnicoSeleccionado ? `${tecnicoSeleccionado.nombre} ${tecnicoSeleccionado.apellido}` : 'Cargando...'" 
                    type="text" 
                    readonly 
                    class="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 cursor-not-allowed font-medium text-sm" 
                  />
                </div>
              </div>
            </div>

            <div class="pt-2">
              <button @click="submitRecepcion" :disabled="!puedeFinalizar || loading" :class="['w-full px-4 py-3 rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 font-bold text-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5', puedeFinalizar && !loading ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed grayscale']">
                <Icon v-if="loading" icon="mdi:loading" class="animate-spin text-xl" />
                <Icon v-else icon="mdi:check-circle-outline" class="text-xl" />
                {{ loading ? 'Sincronizando...' : 'FINALIZAR ORDEN' }}
              </button>
              
              <div v-if="!puedeFinalizar && !loading" class="mt-2 text-center">
                <p class="text-[10px] text-gray-400 italic">Complete los campos requeridos para finalizar</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Banner Compacto -->
        <div class="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-4 text-white shadow-xl relative overflow-hidden group border border-green-500/20">
          <div class="absolute -right-4 -bottom-4 opacity-10 group-hover:opacity-20 transition-all duration-700 rotate-12">
            <Icon icon="mdi:shield-check" class="text-[80px]" />
          </div>
          <div class="relative z-10 flex items-center gap-3">
            <div class="p-2 bg-green-500/20 rounded-xl backdrop-blur-md border border-green-500/30">
              <Icon icon="mdi:shield-lock" class="text-2xl text-green-400" />
            </div>
            <div>
              <h4 class="font-bold text-sm uppercase tracking-tight">Registro de Garantía</h4>
              <p class="text-[10px] text-gray-400 font-medium">Equipos respaldados en tiempo real.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL DE CONFIRMACIÓN (Estilo VentasList) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between" :class="confirmModalHeaderClass">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">{{ confirmModalTitle }}</h3>
                <p class="text-[8px] sm:text-[10px] text-white/80 font-bold">Confirmación requerida</p>
              </div>
            </div>
            <button @click="cancelarConfirmacion" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>

          <div class="p-4 sm:p-6">
            <div class="bg-gray-50 dark:bg-gray-700/30 p-3 sm:p-4 rounded-xl border border-gray-100 dark:border-gray-700 text-center mb-4 sm:mb-6">
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300" v-html="confirmModalMessage"></p>
            </div>

            <div class="flex flex-col sm:flex-row gap-2">
              <button v-if="confirmModalType !== 'info'" @click="cancelarConfirmacion" class="w-full sm:flex-1 px-4 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button @click="aceptarConfirmacion" class="w-full sm:flex-1 px-4 py-2 text-white rounded-lg text-[10px] sm:text-xs font-black uppercase shadow-md transition-all flex items-center justify-center gap-2" :class="confirmModalConfirmClass">
                <Icon :icon="confirmModalIcon" class="text-sm" />
                {{ confirmModalConfirmText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL F2 (PRODUCTOS / TÉCNICOS) -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showF2Modal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="cerrarF2Modal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-zoomIn max-h-[95vh]">
          <!-- Header -->
          <div class="bg-gradient-to-r px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0"
               :class="f2ModalType === 'productos' ? 'from-blue-600 to-indigo-600' : 'from-purple-600 to-violet-600'">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon :icon="f2ModalType === 'productos' ? 'mdi:package-variant' : 'mdi:account-wrench'" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">
                  Seleccionar {{ f2ModalType === 'productos' ? 'Producto / Equipo' : 'Técnico' }}
                </h3>
                <p class="text-[8px] sm:text-[10px] text-white/80 font-bold">
                  {{ f2ModalType === 'productos' ? 'Catálogo de productos y servicios' : 'Personal técnico disponible' }}
                </p>
              </div>
            </div>
            <button @click="cerrarF2Modal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>

          <!-- Buscador -->
          <div class="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <div class="relative">
              <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input v-model="f2ModalSearch" type="text" :placeholder="'Buscar ' + (f2ModalType === 'productos' ? 'por nombre o código...' : 'técnico por nombre...')" class="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm transition-all" />
            </div>
          </div>

          <!-- Lista -->
          <div class="flex-1 overflow-y-auto p-2 custom-scrollbar">
            <div class="grid grid-cols-1 gap-2">
              <template v-if="f2ModalType === 'productos'">
                <div v-for="ps in filtradosF2" :key="ps.idProductoServicio" @click="seleccionarDesdeF2(ps)" class="p-3 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer transition-all hover:shadow-md bg-white dark:bg-gray-800 flex items-center justify-between group">
                  <div class="flex items-center gap-3">
                    <div class="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg group-hover:bg-blue-100">
                      <Icon icon="mdi:barcode" class="text-blue-600" />
                    </div>
                    <div>
                      <p class="font-bold text-sm text-gray-800 dark:text-white">{{ ps.codigo }}</p>
                      <p class="text-xs text-gray-500">{{ ps.nombre }}</p>
                    </div>
                  </div>
                  <Icon icon="mdi:chevron-right" class="text-gray-300 group-hover:text-blue-500" />
                </div>
              </template>
              <template v-else>
                <div v-for="tec in filtradosF2" :key="tec.idTecnico || tec.idUsuario" @click="seleccionarDesdeF2(tec)" class="p-3 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-purple-400 dark:hover:border-purple-500 cursor-pointer transition-all hover:shadow-md bg-white dark:bg-gray-800 flex items-center justify-between group">
                  <div class="flex items-center gap-3">
                    <div class="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg group-hover:bg-purple-100">
                      <Icon icon="mdi:account" class="text-purple-600" />
                    </div>
                    <div>
                      <p class="font-bold text-sm text-gray-800 dark:text-white">{{ tec.nombre?.toUpperCase() }} {{ tec.apellido?.toUpperCase() }}</p>
                      <p class="text-[10px] text-gray-500">{{ tec.nombreUsuario }}</p>
                    </div>
                  </div>
                  <Icon icon="mdi:chevron-right" class="text-gray-300 group-hover:text-purple-500" />
                </div>
              </template>
            </div>

            <div v-if="filtradosF2.length === 0" class="py-12 text-center">
              <Icon icon="mdi:database-search-outline" class="text-5xl text-gray-300 mx-auto mb-2" />
              <p class="text-gray-500 text-sm">No se encontraron resultados</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between flex-shrink-0">
            <p class="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Total: {{ filtradosF2.length }} registros</p>
            <button @click="cerrarF2Modal" class="px-6 sm:px-8 py-2 bg-gray-600 text-white rounded-xl text-[10px] sm:text-xs font-black uppercase shadow-md hover:bg-gray-700 transition-all">Cerrar</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL PAGO EXPRESS -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPagoExpressModal" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="cancelarPagoExpress">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <!-- Header -->
          <div class="bg-gradient-to-r from-yellow-500 to-orange-500 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:cash-fast" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">Pago Diagnóstico Express</h3>
                <p class="text-[8px] sm:text-[10px] text-yellow-100 font-bold">Requiere pago anticipado</p>
              </div>
            </div>
            <button @click="cancelarPagoExpress" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>

          <div class="p-4 sm:p-6 space-y-4">
            <!-- Monto -->
            <div class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-4 text-center border border-yellow-200 dark:border-yellow-800/50">
              <p class="text-[9px] sm:text-[10px] font-black text-yellow-600 dark:text-yellow-400 uppercase tracking-widest mb-1">Monto a Cobrar</p>
              <p class="text-3xl sm:text-4xl font-black text-gray-800 dark:text-white tracking-tight">$10.00</p>
              <p class="text-[9px] sm:text-[10px] text-yellow-600 dark:text-yellow-400 font-bold mt-1">USD — Diagnóstico Prioritario</p>
            </div>

            <!-- Método de pago -->
            <div>
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Método de Pago <span class="text-red-500">*</span></label>
              <div class="grid grid-cols-2 gap-2">
                <button v-for="metodo in metodosPago" :key="metodo.value"
                  @click="pagoExpress.metodoPago = metodo.value"
                  :class="['flex items-center gap-2 p-2.5 rounded-xl border-2 text-sm font-medium transition-all duration-200', pagoExpress.metodoPago === metodo.value ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 shadow-sm' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-yellow-300 hover:bg-yellow-50/50 dark:hover:bg-yellow-900/10']">
                  <Icon :icon="metodo.icon" class="text-base flex-shrink-0" />
                  <span class="text-[10px] sm:text-xs">{{ metodo.label }}</span>
                </button>
              </div>
            </div>

            <!-- Referencia -->
            <div v-if="pagoExpress.metodoPago && pagoExpress.metodoPago !== 'EFECTIVO_USD'">
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">N° Referencia / Confirmación</label>
              <div class="relative">
                <Icon icon="mdi:identifier" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input v-model="pagoExpress.referencia" type="text" placeholder="Ej: 123456789" class="w-full pl-9 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all" />
              </div>
            </div>

            <!-- Info -->
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-3 flex gap-2">
              <Icon icon="mdi:information" class="text-blue-500 text-lg flex-shrink-0 mt-0.5" />
              <p class="text-[10px] sm:text-xs text-blue-700 dark:text-blue-300">Este cobro garantiza atención prioritaria. Si el equipo es reparado, el monto <b>se descuenta</b> del costo total del servicio.</p>
            </div>

            <!-- Cliente -->
            <div class="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 flex items-center gap-3 border border-gray-100 dark:border-gray-700">
              <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Icon icon="mdi:account-check" class="text-green-600 dark:text-green-400 text-base" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-bold text-gray-800 dark:text-white truncate">{{ clienteSeleccionado?.nombre }} {{ clienteSeleccionado?.apellido }}</p>
                <p class="text-[10px] text-gray-500">Orden: {{ numeroOrdenDisplay }}</p>
              </div>
              <div class="text-right">
                <p class="text-xs font-black text-yellow-600">$10.00</p>
                <p class="text-[10px] text-gray-400">Express</p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row gap-2">
            <button @click="cancelarPagoExpress" class="w-full sm:flex-1 px-4 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="confirmarPagoExpress" :disabled="!pagoExpress.metodoPago || procesandoPagoExpress"
              class="w-full sm:flex-1 px-4 py-2 rounded-lg text-[10px] sm:text-xs font-black uppercase shadow-md transition-all flex items-center justify-center gap-2"
              :class="pagoExpress.metodoPago && !procesandoPagoExpress ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'">
              <Icon v-if="procesandoPagoExpress" icon="mdi:loading" class="animate-spin" />
              <Icon v-else icon="mdi:check-circle" />
              {{ procesandoPagoExpress ? 'Procesando...' : 'Confirmar Pago' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL DE CONFIGURACIÓN DE ÍTEM (SERVICIOS Y PRODUCTOS) -->
    <Teleport to="body">
      <div v-if="showItemConfigModal" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-2 sm:p-4 md:p-6" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeItemConfigModal"></div>

        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-auto flex flex-col transform transition-all scale-100 animate-fadeIn max-h-[95vh] overflow-y-auto">
          <div class="p-4 sm:p-5 md:p-6 bg-gradient-to-r" :class="itemConfigTipo === 'servicio' ? 'from-green-600 to-emerald-700' : 'from-blue-600 to-indigo-700'">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-2 sm:gap-3">
                <div class="p-2 bg-white/20 rounded-xl">
                  <Icon :icon="itemConfigTipo === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-xl sm:text-2xl text-white" />
                </div>
                <div>
                  <h3 class="text-base sm:text-lg font-black text-white uppercase tracking-tighter">Configurar {{ itemConfigTipo === 'servicio' ? 'Servicio' : 'Producto' }}</h3>
                  <p class="text-[8px] sm:text-[9px] font-bold text-white/70 uppercase tracking-widest">Ajusta precio, garantía y cantidad</p>
                </div>
              </div>
              <button @click="closeItemConfigModal" class="p-1.5 sm:p-2 hover:bg-white/10 rounded-xl transition-all">
                <Icon icon="mdi:close" class="text-xl sm:text-2xl text-white" />
              </button>
            </div>
          </div>
          
          <div class="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4 bg-gray-50/50 dark:bg-gray-900/50">
            <div class="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <p class="text-[8px] sm:text-[9px] font-black text-gray-400 uppercase tracking-wider mb-1">Ítem seleccionado</p>
              <p class="font-bold text-gray-800 dark:text-white text-xs sm:text-sm break-words">{{ itemConfigSeleccionado?.nombre || '' }}</p>
            </div>

            <div>
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5 sm:mb-2">PRECIO UNITARIO (USD)</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 font-black text-green-600 text-sm">$</span>
                <input 
                  v-model.number="itemConfigPrecio" 
                  type="number" 
                  step="0.01" 
                  min="0.01"
                  class="w-full pl-8 pr-3 py-2.5 sm:py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-base sm:text-lg font-black text-green-600 dark:text-green-400 outline-none focus:border-green-500 transition-all text-right"
                />
              </div>
            </div>

            <div>
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5 sm:mb-2">GARANTÍA (DÍAS)</label>
              <div class="flex gap-2">
                <input 
                  v-model.number="itemConfigGarantia" 
                  type="number" 
                  step="1" 
                  min="0"
                  class="flex-1 px-2 sm:px-3 py-2.5 sm:py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm font-bold outline-none focus:border-green-500 transition-all"
                />
                <div class="px-2 sm:px-3 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 rounded-xl text-[8px] sm:text-[9px] font-black text-gray-500 uppercase tracking-wider flex items-center whitespace-nowrap">
                  {{ formatearGarantia(itemConfigGarantia || 0) }}
                </div>
              </div>
            </div>

            <div>
              <label class="block text-[9px] sm:text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5 sm:mb-2">CANTIDAD</label>
              <div class="flex items-center gap-2 sm:gap-3">
                <button 
                  type="button"
                  @click="itemConfigCantidad = Math.max(1, itemConfigCantidad - 1)"
                  class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center font-black text-base sm:text-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                >
                  <Icon icon="mdi:minus" class="text-base sm:text-lg" />
                </button>
                <input 
                  v-model.number="itemConfigCantidad" 
                  type="number" 
                  step="1" 
                  min="1"
                  class="w-16 sm:w-20 px-2 sm:px-3 py-2.5 sm:py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-center text-base sm:text-lg font-black outline-none focus:border-green-500 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button 
                  type="button"
                  @click="itemConfigCantidad = Math.min(999, itemConfigCantidad + 1)"
                  class="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center font-black text-base sm:text-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                >
                  <Icon icon="mdi:plus" class="text-base sm:text-lg" />
                </button>
                <div class="flex-1 text-right">
                  <p class="text-[7px] sm:text-[8px] text-gray-400 uppercase tracking-wider">TOTAL</p>
                  <p class="text-base sm:text-xl font-black text-green-600 dark:text-green-400">${{ (itemConfigPrecio * itemConfigCantidad).toFixed(2) }}</p>
                </div>
              </div>
            </div>

            <div class="bg-amber-50 dark:bg-amber-900/20 p-2.5 sm:p-3 rounded-xl border border-amber-100 dark:border-amber-800/40 flex gap-2">
              <Icon icon="mdi:information-outline" class="text-amber-500 text-sm shrink-0" />
              <p class="text-[8px] sm:text-[9px] font-bold text-amber-700 dark:text-amber-300 leading-snug">La garantía se aplica sobre el ítem completo. El precio final se multiplica por la cantidad seleccionada.</p>
            </div>
          </div>
          
          <div class="p-3 sm:p-4 md:p-5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
            <button 
              type="button"
              @click="agregarItemConConfiguracion" 
              class="w-full py-3 sm:py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 font-black text-xs sm:text-sm uppercase tracking-wider shadow-lg active:scale-95"
              :class="itemConfigTipo === 'servicio' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'"
            >
              <Icon icon="mdi:check-circle" class="text-base sm:text-lg" />
              AGREGAR AL CLIENTE
            </button>
            <button type="button" @click="closeItemConfigModal" class="w-full mt-2 py-2 text-[9px] sm:text-[10px] font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest transition-all">
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL DE SELECCIÓN DE ÍTEM DEL CATÁLOGO -->
    <Teleport to="body">
      <div v-if="showCatalogModal" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-2 sm:p-4 md:p-6" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showCatalogModal = false"></div>

        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[92vh] flex flex-col overflow-hidden transform transition-all scale-100 animate-fadeIn">
          <div class="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-center">
              <h3 class="text-base sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <div class="p-1.5 sm:p-2 rounded-lg" :class="catalogType === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'">
                  <Icon :icon="catalogType === 'servicio' ? 'mdi:wrench' : 'mdi:package-variant'" class="text-lg sm:text-xl" />
                </div>
                <span class="text-sm sm:text-base">Seleccionar {{ catalogType === 'servicio' ? 'Servicio' : 'Producto' }}</span>
              </h3>
              <button @click="showCatalogModal = false" class="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">
                <Icon icon="mdi:close" class="text-xl sm:text-2xl text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div class="mt-3 sm:mt-4">
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="catalogSearch" type="text" placeholder="Buscar por nombre..." class="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 font-medium outline-none text-sm" autocomplete="off" />
              </div>
            </div>
          </div>
          
          <div class="p-3 sm:p-4 md:p-6 overflow-y-auto flex-1 custom-scrollbar">
            <div v-if="filteredCatalogItems.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div v-for="item in filteredCatalogItems" :key="item.id || item.idServicio || item.idProducto" @click="seleccionarItemCatalogo(item)" class="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] bg-white dark:bg-gray-800" :class="catalogType === 'servicio' ? 'hover:border-green-500' : 'hover:border-blue-500'">
                <div class="flex gap-2 sm:gap-3">
                  <div class="p-2 sm:p-3 rounded-lg h-fit" :class="catalogType === 'servicio' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'">
                    <Icon :icon="catalogType === 'servicio' ? 'mdi:wrench' : 'mdi:package'" class="text-xl sm:text-2xl" :class="catalogType === 'servicio' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4 class="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">{{ item.nombre }}</h4>
                    <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ catalogType === 'servicio' ? 'Servicio Técnico' : 'Producto' }}</p>
                    
                    <div class="mt-2 sm:mt-3 flex flex-wrap justify-between items-center gap-1">
                      <span class="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Precio USD:</span>
                      <span class="text-base sm:text-lg font-bold" :class="catalogType === 'servicio' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'">${{ (catalogType === 'servicio' ? (item.precioUsd || 0) : (item.precio_venta_usd || 0)).toFixed(2) }}</span>
                    </div>
                    <div v-if="(catalogType === 'servicio' && item.garantiaDias) || (catalogType === 'producto' && item.garantia_meses)" class="mt-1 sm:mt-2">
                      <span class="text-[8px] sm:text-[9px] font-black bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-1.5 sm:px-2 py-0.5 rounded-full inline-block">
                        🛡️ Garantía: {{ catalogType === 'servicio' ? formatearGarantia(item.garantiaDias) : formatearGarantia((item.garantia_meses || 0) * 30) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-8 sm:py-12">
              <Icon icon="mdi:magnify-close" class="text-4xl sm:text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-gray-600 dark:text-gray-400 text-sm sm:text-base">No se encontraron resultados</p>
            </div>
          </div>
          
          <div class="p-3 sm:p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div class="flex flex-wrap justify-between items-center gap-2">
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Seleccione un elemento para configurarlo</p>
              <button @click="showCatalogModal = false" class="px-4 sm:px-6 py-2 sm:py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 font-medium text-xs sm:text-sm">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { notificarRecepcionExpress } from '@/utils/notificacionesHelper';
import api from '@/api/recepcionEquipo.js';
import { obtenerClientes, crearCliente, formatearDocumento, formatearTelefonoVenezolano } from '@/api/clientes.js';
import { obtenerProductoServicios } from '@/api/productoServicio.js';
import { obtenerEstadosRecepcion } from '@/api/estadoRecepcion.js';
import { obtenerSedesActivas } from '@/api/sede.js';
import { obtenerUsuarios } from '@/api/usuarios.js';
import { obtenerTecnicos, obtenerTecnicoPorIdUsuario } from '@/api/tecnicos.js';
import { getCurrentUser } from "@/utils/permissionUtils";
import { imprimirTicketDiagnostico } from '@/utils/ticketGenerator.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { obtenerServicios } from '@/api/servicios.js';
import { obtenerProductos } from '@/api/productos.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { crearSalidaEquipo } from '@/api/salidaEquipoDetalle.js';
import { crearPagoSalidaEquipo } from '@/api/pagoSalidaEquipo.js';

export default {
  name: 'RecepcionExpress',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: false,
      pollingActive: false,
      pollingInterval: null,
      buscarCliente: '',
      clienteSeleccionado: null,
      clientesEncontrados: [],
      cargandoClientes: false,
      mostrarListaClientes: false,
      timeoutBusqueda: null,
      todosLosClientes: [],
      modoCliente: 'buscar',
      nuevoCliente: { tipoDocumento: '', documento: '', nombre: '', apellido: '', telefono: '', email: '', direccion: '', estado: 'activo' },
      guardandoCliente: false,
      productoServicios: [],
      buscarProducto: '',
      productosEncontrados: [],
      productoSeleccionado: null,
      cargandoProductos: false,
      mostrarListaProductos: false,
      estados: [],
      sedesList: [],
      tecnicos: [],
      buscarTecnico: '',
      tecnicosEncontrados: [],
      cargandoTecnicos: false,
      mostrarListaTecnicos: false,
      tecnicoSeleccionado: null,
      serviciosList: [],
      catServicios: [],
      catProductos: [],
      itemsAgregados: [],
      nombreItemManual: '',
      precioItemManual: 0,
      showCatalogModal: false,
      catalogType: 'servicio',
      catalogSearch: '',
      showItemConfigModal: false,
      itemConfigTipo: 'servicio',
      itemConfigSeleccionado: null,
      itemConfigPrecio: 0,
      itemConfigGarantia: 0,
      itemConfigCantidad: 1,
      tasaActual: 1,
      idTasaVenta: 1,
      idFuenteTasa: 1,
      pagoInicial: {
        servicioNombre: '',
        costoEstimado: 0,
        modalidad: 'TOTAL',
        montoCobrar: 0,
        metodoPago: 'EFECTIVO_USD',
        referencia: ''
      },
      form: {
        idProductoServicios: 0,
        fechaIngreso: new Date().toLocaleString('sv-SE').replace(' ', 'T').slice(0, 16),
        fechaReparacion: '',
        fechaEntrega: '',
        fechaPago: '',
        descripcion: '',
        problema: '',
        accesorios: '',
        idCliente: 0,
        idUsuario: parseInt(localStorage.getItem('idUsuario') || 1),
        idTecnicoAsignado: 0,
        idEstadoRecepcion: 0,
        detalles: [],
        clavePatron: '',
        taller: '',
        idSede: null,
        tipoDiagnostico: 'GRATIS',
        montoDiagnostico: 0,
        actividadesRealizadas: ''
      },
      numeroOrdenDisplay: '',
      showF2Modal: false,
      f2ModalType: 'productos',
      f2ModalSearch: '',
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      confirmModalIcon: 'mdi:check-circle',
      confirmModalHeaderClass: 'from-green-600 to-emerald-600',
      confirmModalConfirmText: 'Aceptar',
      confirmModalType: 'success',
      confirmModalConfirmClass: 'bg-green-600 hover:bg-green-700',
      confirmCallback: null,
      confirmCancelCallback: null,
      showPagoExpressModal: false,
      procesandoPagoExpress: false,
      pagoExpress: {
        metodoPago: '',
        referencia: ''
      },
      metodosPago: [
        { value: 'EFECTIVO_USD',    label: 'Efectivo USD',    icon: 'mdi:cash-usd' },
        { value: 'TRANSFERENCIA',   label: 'Transferencia',   icon: 'mdi:bank-transfer' },
        { value: 'PAGO_MOVIL',      label: 'Pago Móvil',     icon: 'mdi:cellphone-nfc' },
        { value: 'ZELLE',           label: 'Zelle',           icon: 'mdi:send-circle' },
        { value: 'BINANCE',         label: 'Binance/Cripto',  icon: 'mdi:currency-btc' },
        { value: 'PUNTO_VENTA',     label: 'Punto de Venta',  icon: 'mdi:credit-card' }
      ],
      currentUser: null
    };
  },
  computed: {
    subtotalItemsAgregados() {
      return this.itemsAgregados.reduce((acc, it) => acc + (Number(it.precio_total || it.precio || 0)), 0);
    },
    filteredCatalogItems() {
      const search = (this.catalogSearch || '').toLowerCase().trim();
      const list = this.catalogType === 'servicio' ? this.catServicios : this.catProductos;
      if (!search) return list.slice(0, 50);
      return list.filter(item => 
        (item.nombre || '').toLowerCase().includes(search) || 
        (item.codigo || '').toLowerCase().includes(search)
      ).slice(0, 50);
    },
    filtradosF2() {
      const termino = this.f2ModalSearch.toLowerCase().trim();
      if (this.f2ModalType === 'productos') {
        if (!termino) return this.productoServicios.slice(0, 50);
        return this.productoServicios.filter(ps => 
          (ps.nombre || '').toLowerCase().includes(termino) || 
          (ps.codigo || '').toLowerCase().includes(termino)
        ).slice(0, 50);
      } else {
        if (!termino) return this.tecnicos.slice(0, 50);
        return this.tecnicos.filter(tec => 
          `${tec.nombre} ${tec.apellido}`.toLowerCase().includes(termino)
        ).slice(0, 50);
      }
    },
    puedeFinalizar() {
      return (
        this.clienteSeleccionado &&
        this.form.idProductoServicios !== 0 &&
        this.form.descripcion.trim() !== '' &&
        this.form.problema.trim() !== '' &&
        this.form.idTecnicoAsignado !== 0 &&
        this.form.idSede !== null &&
        this.form.idSede !== 'null' &&
        this.form.accesorios.trim() !== ''
      );
    },
    puedeGuardarCliente() { 
      return (this.nuevoCliente.tipoDocumento && this.nuevoCliente.documento && this.nuevoCliente.documento.trim() !== '' && this.nuevoCliente.nombre && this.nuevoCliente.nombre.trim() !== '' && this.nuevoCliente.direccion && this.nuevoCliente.direccion.trim() !== ''); 
    },
    montoCobrarCalculado() {
      if (this.form.tipoDiagnostico === 'ADELANTADO') {
        if (this.pagoInicial.modalidad === 'TOTAL') {
          return this.subtotalItemsAgregados;
        }
        if (this.pagoInicial.modalidad === 'ABONO') {
          return Math.min(this.subtotalItemsAgregados, Math.max(0, Number(this.pagoInicial.montoCobrar || 0)));
        }
        return this.subtotalItemsAgregados;
      }
      if (this.pagoInicial.modalidad === 'EXPRESS' || this.form.tipoDiagnostico === 'EXPRESS') return 10;
      return 0;
    },
    saldoRestanteCalculado() {
      if (this.form.tipoDiagnostico === 'ADELANTADO') {
        const total = this.subtotalItemsAgregados;
        const cobrado = this.montoCobrarCalculado;
        return Math.max(0, total - cobrado);
      }
      return 0;
    },
    montoCobrarBsCalculado() {
      const bs = this.montoCobrarCalculado * (this.tasaActual || 1);
      return bs.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  },
  async mounted() {
    await this.cargarCatalogos();
    await this.generarNumeroOrdenDisplay();
    
    // 🔥 INICIAR POLLING CADA 10 SEGUNDOS
    this.pollingActive = true;
    this.pollingInterval = setInterval(() => {
      this.generarNumeroOrdenDisplay();
    }, 10000);
  },
  beforeDestroy() {
    // 🔥 LIMPIAR EL INTERVALO AL DESTRUIR EL COMPONENTE
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.pollingActive = false;
    }
  },
  methods: {
    toTitleCase,
    mostrarConfirmacion(options) {
      this.confirmModalTitle = options.title || 'Confirmar';
      this.confirmModalMessage = options.message || '¿Estás seguro?';
      this.confirmModalIcon = options.icon || 'mdi:help-circle';
      this.confirmModalType = options.type || 'confirm';
      this.confirmModalConfirmText = options.confirmText || 'Aceptar';
      
      if (options.type === 'danger') {
        this.confirmModalHeaderClass = 'from-red-600 to-rose-600';
        this.confirmModalConfirmClass = 'bg-red-600 hover:bg-red-700';
      } else if (options.type === 'success') {
        this.confirmModalHeaderClass = 'from-green-600 to-emerald-600';
        this.confirmModalConfirmClass = 'bg-green-600 hover:bg-green-700';
      } else {
        this.confirmModalHeaderClass = 'from-blue-600 to-indigo-600';
        this.confirmModalConfirmClass = 'bg-blue-600 hover:bg-blue-700';
      }
      
      this.confirmCallback = options.onConfirm || null;
      this.confirmCancelCallback = options.onCancel || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() { this.showConfirmModal = false; if (this.confirmCancelCallback) this.confirmCancelCallback(); },
    aceptarConfirmacion() { this.showConfirmModal = false; if (this.confirmCallback) this.confirmCallback(); },

    mostrarModalF2Productos() {
      this.f2ModalType = 'productos';
      this.f2ModalSearch = '';
      this.showF2Modal = true;
    },
    mostrarModalF2Tecnicos() {
      this.f2ModalType = 'tecnicos';
      this.f2ModalSearch = '';
      this.showF2Modal = true;
    },
    cerrarF2Modal() { this.showF2Modal = false; },
    seleccionarDesdeF2(item) {
      if (this.f2ModalType === 'productos') {
        this.seleccionarProducto(item);
      } else {
        this.seleccionarTecnico(item);
      }
      this.cerrarF2Modal();
    },

    async cargarCatalogos() {
      console.log("🚀 Cargando catálogos para RecepcionExpress...");
      
      const executeApi = async (apiFunc, label) => {
        try {
          const res = await apiFunc();
          if (!res.success) console.warn(`⚠️ ${label} cargado con advertencia:`, res.error || res.message);
          return res;
        } catch (err) {
          console.error(`❌ Error cargando ${label}:`, err);
          return { success: false, error: err.message, data: [] };
        }
      };

      const [cRes, uRes, eRes, psRes, tRes, sdRes, srvRes, tasaRes, prodRes] = await Promise.all([
        executeApi(() => obtenerClientes(), "Clientes"),
        executeApi(() => obtenerUsuarios('activos'), "Usuarios"),
        executeApi(() => obtenerEstadosRecepcion(true), "Estados"),
        executeApi(() => obtenerProductoServicios(), "Productos/Servicios"),
        executeApi(() => obtenerTecnicos(), "Técnicos"),
        executeApi(() => obtenerSedesActivas(), "Sedes"),
        executeApi(() => obtenerServicios('activos'), "Servicios"),
        executeApi(() => obtenerTasaVentaActual(), "Tasa Venta Actual"),
        executeApi(() => obtenerProductos('activos'), "Productos")
      ]);
      
      if (cRes.success) this.todosLosClientes = cRes.data || [];
      if (sdRes.success) this.sedesList = sdRes.data || [];
      if (psRes.success) this.productoServicios = psRes.data || [];
      if (tRes.success) this.tecnicos = tRes.data || [];
      if (srvRes.success) {
        this.serviciosList = srvRes.data || [];
        this.catServicios = srvRes.data || [];
      }
      if (prodRes.success) {
        this.catProductos = prodRes.data || [];
      }
      if (tasaRes.success && tasaRes.data) {
        this.tasaActual = Number(tasaRes.data.precio_usd_bs || tasaRes.data.precio || 1);
        this.idTasaVenta = tasaRes.data.idTasaVenta || 1;
        this.idFuenteTasa = tasaRes.data.idFuenteTasa || 1;
      }

      this.currentUser = getCurrentUser();
      if (this.currentUser) {
        const idUsuario = this.currentUser.idUsuario || this.currentUser.id;
        const tecRes = await obtenerTecnicoPorIdUsuario(idUsuario);
        if (tecRes.success && tecRes.data) {
          this.seleccionarTecnico(tecRes.data);
        } else {
          this.tecnicoSeleccionado = {
            nombre: this.currentUser.nombre || 'Admin',
            apellido: this.currentUser.apellido || '',
            idTecnico: 0
          };
          this.form.idTecnicoAsignado = this.currentUser.idUsuario || this.currentUser.id;
        }
      }
      
      if (tRes.success && tRes.data?.length > 0) {
        this.tecnicos = tRes.data;
      } else if (uRes.success && uRes.data?.length > 0) {
        this.tecnicos = uRes.data.filter(u => 
          u.idRol === 3 || u.rolId === 3 || u.id_rol === 3 || u.tipo_tecnico === true ||
          u.nombre?.toLowerCase().includes('tec') ||
          (u.rol && u.rol.nombre?.toLowerCase().includes('tec'))
        );
      }

      if (this.tecnicos.length > 0) {
        this.tecnicosEncontrados = this.tecnicos.slice(0, 10);
        const idUsuarioActual = parseInt(localStorage.getItem('idUsuario'));
        if (idUsuarioActual && !this.form.idTecnicoAsignado) {
          const tecActual = this.tecnicos.find(t => t.idUsuario === idUsuarioActual);
          if (tecActual) this.seleccionarTecnico(tecActual);
        }
      }

      if (eRes.success && eRes.data?.length > 0) {
        this.estados = eRes.data;
        const estPorDiag = this.estados.find(e => e.nombre?.toUpperCase().includes('POR DIAGNOSTICAR'));
        if (estPorDiag) {
          this.form.idEstadoRecepcion = estPorDiag.idEstadoRecepcion;
        } else {
          this.form.idEstadoRecepcion = this.estados[0].idEstadoRecepcion;
        }
      }
      
      console.log("✅ Catálogos procesados:", {
        clientes: this.todosLosClientes.length,
        tecnicos: this.tecnicos.length,
        productos: this.productoServicios.length,
        sedes: this.sedesList.length
      });
    },

    // 🔥 GENERAR NÚMERO DE ORDEN PARA MOSTRAR EN UI
    async generarNumeroOrdenDisplay() {
      try {
        const res = await api.obtenerUltimoNumeroOrden();
        if (res.success) {
          const lastNum = parseInt(res.data);
          const nextNum = (isNaN(lastNum) || lastNum < 5999) ? 6000 : lastNum + 1;
          this.numeroOrdenDisplay = nextNum.toString();
          console.log(`📊 [FRONTEND] Último número: ${lastNum}, Siguiente: ${nextNum}`);
        } else {
          this.numeroOrdenDisplay = '6000';
        }
      } catch (error) {
        console.error("Error generando número de orden:", error);
        this.numeroOrdenDisplay = '6000';
      }
    },

    debounceBuscarClientes() { this.mostrarListaClientes = true; if (this.timeoutBusqueda) clearTimeout(this.timeoutBusqueda); this.timeoutBusqueda = setTimeout(() => this.buscarClientesLocal(), 300); },
    mostrarClientesSiHay() {
      if (!this.buscarCliente || this.buscarCliente.trim() === '') {
        this.clientesEncontrados = this.todosLosClientes.slice(0, 10);
      }
      if (this.clientesEncontrados.length > 0) this.mostrarListaClientes = true;
    },
    buscarClientesLocal() { 
      if (!this.buscarCliente || this.buscarCliente.trim() === '') {
        this.clientesEncontrados = this.todosLosClientes.slice(0, 10);
        this.mostrarListaClientes = this.clientesEncontrados.length > 0;
        this.cargandoClientes = false;
        return;
      } 
      this.cargandoClientes = true; 
      const termino = this.buscarCliente.toLowerCase().trim(); 
      setTimeout(() => { 
        this.clientesEncontrados = this.todosLosClientes.filter(cliente => { 
          const nombreCompleto = `${cliente.nombre || ''} ${cliente.apellido || ''}`.toLowerCase(); 
          const documento = (cliente.documento || '').toLowerCase();
          const telefono = (cliente.telefono || '').toLowerCase();
          const email = (cliente.email || '').toLowerCase();
          return nombreCompleto.includes(termino) || documento.includes(termino) || telefono.includes(termino) || email.includes(termino);
        }).slice(0, 10); 
        this.cargandoClientes = false; 
      }, 300); 
    },

    debounceBuscarProductos() { this.mostrarListaProductos = true; if (this.timeoutBusqueda) clearTimeout(this.timeoutBusqueda); this.timeoutBusqueda = setTimeout(() => this.buscarProductosLocal(), 300); },
    mostrarProductosSiHay() { if (this.productosEncontrados.length > 0) this.mostrarListaProductos = true; },
    buscarProductosLocal() {
      if (!this.buscarProducto || this.buscarProducto.trim() === '') { this.productosEncontrados = []; this.mostrarListaProductos = false; return; }
      this.cargandoProductos = true;
      const termino = this.buscarProducto.toLowerCase().trim();
      setTimeout(() => {
        this.productosEncontrados = this.productoServicios.filter(ps => {
          const normalize = (str) => (str || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const terms = normalize(this.buscarProducto).split(/\s+/).filter(Boolean);
          const searchableText = normalize(`${ps.nombre} ${ps.codigo || ''} ${ps.descripcion || ''}`);
          return terms.every(term => searchableText.includes(term));
        }).slice(0, 15);
        this.cargandoProductos = false;
      }, 300);
    },

    debounceBuscarTecnicos() { this.mostrarListaTecnicos = true; if (this.timeoutBusqueda) clearTimeout(this.timeoutBusqueda); this.timeoutBusqueda = setTimeout(() => this.buscarTecnicosLocal(), 300); },
    mostrarTecnicosSiHay() { 
      if (this.tecnicos.length > 0) {
        if (!this.buscarTecnico) {
          this.tecnicosEncontrados = this.tecnicos.slice(0, 10);
        }
        this.mostrarListaTecnicos = true; 
      }
    },
    buscarTecnicosLocal() {
      if (!this.buscarTecnico || this.buscarTecnico.trim() === '') { 
        this.tecnicosEncontrados = this.tecnicos.slice(0, 10); 
        return; 
      }
      this.cargandoTecnicos = true;
      const termino = this.buscarTecnico.toLowerCase().trim();
      setTimeout(() => {
        this.tecnicosEncontrados = this.tecnicos.filter(tec => {
          const nombreCompleto = `${tec.nombre || ''} ${tec.apellido || ''}`.toLowerCase();
          return nombreCompleto.includes(termino);
        }).slice(0, 10);
        this.cargandoTecnicos = false;
      }, 300);
    },

    seleccionarTecnico(tec) {
      this.tecnicoSeleccionado = tec;
      this.form.idTecnicoAsignado = tec.idTecnico || tec.idUsuario;
      this.buscarTecnico = '';
      this.tecnicosEncontrados = [];
      this.mostrarListaTecnicos = false;
      this.toast.success(`Técnico ${tec.nombre} asignado`);
    },

    deseleccionarTecnico() {
      this.tecnicoSeleccionado = null;
      this.form.idTecnicoAsignado = 0;
      this.buscarTecnico = '';
      this.toast.info('Técnico removido');
    },

    seleccionarProducto(ps) {
      this.productoSeleccionado = ps;
      this.form.idProductoServicios = ps.idProductoServicios || ps.idProductoServicio || ps.id || 0;
      this.form.descripcion = ps.descripcion || '';
      this.buscarProducto = '';
      this.productosEncontrados = [];
      this.mostrarListaProductos = false;
      this.toast.success(`Producto ${ps.nombre} seleccionado`);
    },

    deseleccionarProducto() {
      this.productoSeleccionado = null;
      this.form.idProductoServicios = 0;
      this.form.descripcion = '';
      this.buscarProducto = '';
      this.toast.info('Producto removido');
    },

    seleccionarCliente(cliente) {
      if (!cliente) return;
      console.log("🎯 Seleccionando cliente:", cliente);
      const id = cliente.idCliente || cliente.id || cliente.id_cliente;
      if (!id) {
        console.error("❌ El cliente seleccionado no tiene un ID válido:", cliente);
        this.toast.error("Error al seleccionar: ID de cliente no encontrado");
        return;
      }
      this.clienteSeleccionado = { ...cliente, idCliente: id };
      this.form.idCliente = id;
      this.buscarCliente = '';
      this.clientesEncontrados = [];
      this.mostrarListaClientes = false;
      console.log("✅ Cliente seleccionado exitosamente:", this.clienteSeleccionado);
      this.toast.success(`Cliente ${cliente.nombre || 'seleccionado'} activado`);
    },

    deseleccionarCliente() { 
      this.clienteSeleccionado = null; 
      this.form.idCliente = null; 
      this.buscarCliente = ''; 
      this.toast.info('Cliente removido'); 
    },

    async guardarNuevoCliente() {
      if (!this.puedeGuardarCliente) { 
        this.toast.error('Por favor complete los campos obligatorios (Tipo, Documento, Nombre y Dirección)'); 
        return; 
      }
      this.guardandoCliente = true;
      try {
        const response = await crearCliente(this.nuevoCliente);
        if (response.success) {
          let clienteParaSeleccionar = response.data || { ...this.nuevoCliente };
          const buscarId = (obj) => {
            if (!obj) return null;
            return obj.idCliente || obj.id || obj.id_cliente || obj.IdCliente || obj.Id || obj.ID || obj.id_Cliente;
          };
          const idRespuesta = buscarId(response) || buscarId(response.data);
          if (idRespuesta) {
            clienteParaSeleccionar.idCliente = idRespuesta;
          }
          clienteParaSeleccionar.nombre = clienteParaSeleccionar.nombre || this.nuevoCliente.nombre;
          clienteParaSeleccionar.apellido = clienteParaSeleccionar.apellido || this.nuevoCliente.apellido;
          clienteParaSeleccionar.documento = clienteParaSeleccionar.documento || this.nuevoCliente.documento;
          clienteParaSeleccionar.tipoDocumento = clienteParaSeleccionar.tipoDocumento || this.nuevoCliente.tipoDocumento;

          if (Array.isArray(this.todosLosClientes)) {
            const existe = this.todosLosClientes.find(c => c.idCliente === clienteParaSeleccionar.idCliente);
            if (!existe) {
              this.todosLosClientes.push(clienteParaSeleccionar);
            }
          }
          this.modoCliente = 'buscar';
          setTimeout(() => {
            this.seleccionarCliente(clienteParaSeleccionar);
          }, 100);
          this.nuevoCliente = { 
            tipoDocumento: 'cedula', 
            documento: '', 
            nombre: '', 
            apellido: '', 
            telefono: '', 
            email: '', 
            direccion: '', 
            estado: 'activo' 
          };
          this.toast.success(`Cliente "${clienteParaSeleccionar.nombre}" creado exitosamente`);
        } else {
          throw new Error(response.error || response.message || 'Error al procesar la respuesta del servidor');
        }
      } catch (error) {
        console.error("❌ [ERROR] Falló la creación del cliente:", error);
        this.toast.error('No se pudo crear el cliente: ' + (error.message || 'Error de comunicación'));
      } finally {
        this.guardandoCliente = false;
      }
    },

    formatearTipoDocumento(tipo) { const tipos = { 'cedula': 'Cédula', 'rif': 'RIF', 'pasaporte': 'Pasaporte', 'identificacion_extranjera': 'Identificación Extranjera' }; return tipos[tipo?.toLowerCase()] || tipo || 'Sin documento'; },
    getPlaceholderDocumento(tipo) { const placeholders = { 'cedula': 'V-31.757.818', 'rif': 'J-12345678-9', 'pasaporte': 'ABC123456', 'identificacion_extranjera': 'Número' }; return placeholders[tipo] || 'Documento'; },
    formatearDocumentoInput() { if (this.nuevoCliente.tipoDocumento && this.nuevoCliente.documento) this.nuevoCliente.documento = formatearDocumento(this.nuevoCliente.tipoDocumento, this.nuevoCliente.documento); },
    formatearTelefonoInput() { if (this.nuevoCliente.telefono) this.nuevoCliente.telefono = formatearTelefonoVenezolano(this.nuevoCliente.telefono); },
    limitarAccesorios() {
      if (this.form.accesorios && this.form.accesorios.length > 200) {
        this.form.accesorios = this.form.accesorios.slice(0, 200);
      }
    },

    seleccionarServicioPredefinido(s) {
      this.pagoInicial.servicioNombre = s.nombre;
      this.pagoInicial.costoEstimado = Number(s.precioUsd || s.precio || 0);
      if (!this.form.descripcion) {
        this.form.descripcion = s.nombre;
      }
      this.onCostoEstimadoChange();
    },

    onCostoEstimadoChange() {
      if (this.pagoInicial.modalidad === 'TOTAL') {
        this.pagoInicial.montoCobrar = Number(this.pagoInicial.costoEstimado || 0);
      } else if (this.pagoInicial.modalidad === 'ABONO') {
        if (!this.pagoInicial.montoCobrar || this.pagoInicial.montoCobrar > this.pagoInicial.costoEstimado) {
          this.pagoInicial.montoCobrar = Math.round(Number(this.pagoInicial.costoEstimado || 0) / 2);
        }
      }
    },

    abrirModalConfiguracionItem(tipo) {
      this.itemConfigTipo = tipo;
      this.catalogType = tipo;
      this.catalogSearch = '';
      this.showCatalogModal = true;
    },

    seleccionarItemCatalogo(item) {
      this.itemConfigSeleccionado = item;
      if (this.itemConfigTipo === 'servicio') {
        this.itemConfigPrecio = item.precioUsd || 0;
        this.itemConfigGarantia = item.garantiaDias || 0;
      } else {
        this.itemConfigPrecio = item.precio_venta_usd || 0;
        this.itemConfigGarantia = (item.garantia_meses || 0) * 30;
      }
      this.itemConfigCantidad = 1;
      this.showCatalogModal = false;
      this.showItemConfigModal = true;
    },

    agregarItemConConfiguracion() {
      if (!this.itemConfigSeleccionado) return;
      if (this.itemConfigPrecio <= 0) {
        this.toast.warning("El precio debe ser mayor a cero");
        return;
      }
      if (this.itemConfigCantidad < 1) {
        this.toast.warning("La cantidad debe ser al menos 1");
        return;
      }
      const precioTotal = this.itemConfigPrecio * this.itemConfigCantidad;
      const nuevoItem = {
        uuid: Math.random().toString(36).substring(7),
        id: this.itemConfigSeleccionado.id || this.itemConfigSeleccionado.idServicio || this.itemConfigSeleccionado.idProducto,
        tipo: this.itemConfigTipo,
        nombre: this.itemConfigSeleccionado.nombre,
        precio_unitario: this.itemConfigPrecio,
        precio_total: precioTotal,
        precio: precioTotal,
        cantidad: this.itemConfigCantidad,
        garantia: this.itemConfigGarantia,
        costo: this.itemConfigTipo === 'servicio'
          ? (this.itemConfigSeleccionado.costoManoObraUsd || 0)
          : (this.itemConfigSeleccionado.precio_compra_usd || 0),
        comision: this.itemConfigSeleccionado.comisionTecnico || 0
      };
      this.itemsAgregados.push(nuevoItem);
      if (this.pagoInicial.modalidad === 'TOTAL') {
        this.pagoInicial.montoCobrar = this.subtotalItemsAgregados;
      }
      if (this.itemConfigGarantia > 0) {
        this.toast.success(`✅ Garantía de ${this.formatearGarantia(this.itemConfigGarantia)} aplicada`);
      }
      this.closeItemConfigModal();
    },

    closeItemConfigModal() {
      this.showItemConfigModal = false;
      this.itemConfigSeleccionado = null;
      this.itemConfigPrecio = 0;
      this.itemConfigGarantia = 0;
      this.itemConfigCantidad = 1;
    },

    actualizarDesdePrecioTotalItem(item) {
      if (item.cantidad > 0 && item.precio_total > 0) {
        item.precio_unitario = item.precio_total / item.cantidad;
        item.precio = item.precio_total;
      }
    },

    agregarItemManual() {
      if (!this.nombreItemManual || !this.nombreItemManual.trim()) {
        this.toast.warning('Ingrese el nombre del repuesto o servicio');
        return;
      }
      if (!this.precioItemManual || this.precioItemManual <= 0) {
        this.toast.warning('Ingrese un precio mayor a $0');
        return;
      }
      const nuevo = {
        uuid: Math.random().toString(36).substring(7),
        id: null,
        tipo: 'repuesto',
        nombre: this.nombreItemManual.trim(),
        precio_unitario: parseFloat(this.precioItemManual),
        precio_total: parseFloat(this.precioItemManual),
        precio: parseFloat(this.precioItemManual),
        cantidad: 1,
        garantia: 0,
        costo: 0,
        comision: 0
      };
      this.itemsAgregados.push(nuevo);
      this.nombreItemManual = '';
      this.precioItemManual = 0;
      if (this.pagoInicial.modalidad === 'TOTAL') {
        this.pagoInicial.montoCobrar = this.subtotalItemsAgregados;
      }
      this.toast.success('Ítem agregado correctamente');
    },

    eliminarItemAgregado(uuid) {
      this.itemsAgregados = this.itemsAgregados.filter(it => it.uuid !== uuid);
      if (this.pagoInicial.modalidad === 'TOTAL') {
        this.pagoInicial.montoCobrar = this.subtotalItemsAgregados;
      }
    },

    formatearGarantia(dias) {
      if (!dias || dias === 0) return 'Sin garantía';
      if (dias >= 365) {
        const anios = Math.floor(dias / 365);
        return `${anios} ${anios === 1 ? 'año' : 'años'}`;
      }
      if (dias >= 30) {
        const meses = Math.floor(dias / 30);
        return `${meses} ${meses === 1 ? 'mes' : 'meses'}`;
      }
      return `${dias} ${dias === 1 ? 'día' : 'días'}`;
    },

    cambiarModalidadPago(mod) {
      this.pagoInicial.modalidad = mod;
      if (mod === 'TOTAL') {
        this.pagoInicial.montoCobrar = this.subtotalItemsAgregados;
        if (!this.pagoInicial.metodoPago) this.pagoInicial.metodoPago = 'EFECTIVO_USD';
      } else if (mod === 'ABONO') {
        this.pagoInicial.montoCobrar = +(this.subtotalItemsAgregados / 2).toFixed(2);
        if (!this.pagoInicial.metodoPago) this.pagoInicial.metodoPago = 'EFECTIVO_USD';
      } else if (mod === 'EXPRESS') {
        this.form.tipoDiagnostico = 'EXPRESS';
        this.form.montoDiagnostico = 10;
        this.pagoInicial.montoCobrar = 10;
        if (!this.pagoInicial.metodoPago) this.pagoInicial.metodoPago = 'EFECTIVO_USD';
      } else {
        this.pagoInicial.montoCobrar = 0;
      }
    },

    async submitRecepcion() {
      if (!this.puedeFinalizar) {
        this.toast.error('Por favor complete los campos obligatorios');
        return;
      }

      if (this.form.tipoDiagnostico === 'ADELANTADO') {
        if (this.itemsAgregados.length === 0) {
          this.toast.warning('Debe asignar al menos un servicio o producto para la recepción con pago adelantado');
          return;
        }
        if (!this.pagoInicial.metodoPago) {
          this.toast.warning('Seleccione el método de pago para el cobro adelantado');
          return;
        }
      }

      const montoCobro = this.montoCobrarCalculado;
      if (this.form.tipoDiagnostico === 'ADELANTADO' || (this.pagoInicial.modalidad !== 'NINGUNO' && montoCobro > 0)) {
        if (!this.pagoInicial.metodoPago) {
          this.toast.warning('Seleccione el método de pago para el cobro en recepción');
          return;
        }
        const metodoLabel = this.metodosPago.find(m => m.value === this.pagoInicial.metodoPago)?.label || this.pagoInicial.metodoPago;
        this.mostrarConfirmacion({
          title: 'Confirmar Cobro y Recepción',
          message: `¿Desea registrar la orden <b>${this.numeroOrdenDisplay}</b> con cobro de <b>$${montoCobro.toFixed(2)}</b> (${metodoLabel}) para <b>${this.clienteSeleccionado.nombre}</b>?`,
          icon: 'mdi:cash-check',
          confirmText: 'Sí, cobrar y registrar',
          confirmModalHeaderClass: 'from-emerald-600 to-teal-600',
          confirmModalConfirmClass: 'bg-emerald-600 hover:bg-emerald-700',
          onConfirm: () => this._procesarRegistroRecepcion()
        });
        return;
      }

      // Si es Diagnóstico Express clásico sin card
      if (this.form.tipoDiagnostico === 'EXPRESS' && this.pagoInicial.modalidad === 'NINGUNO') {
        this.pagoExpress = { metodoPago: '', referencia: '' };
        this.showPagoExpressModal = true;
        return;
      }

      this.mostrarConfirmacion({
        title: 'Confirmar Recepción',
        message: `¿Desea registrar la orden <b>${this.numeroOrdenDisplay}</b> para <b>${this.clienteSeleccionado.nombre}</b>?`,
        icon: 'mdi:flash',
        confirmText: 'Sí, registrar',
        onConfirm: () => this._procesarRegistroRecepcion()
      });
    },

    cancelarPagoExpress() {
      this.showPagoExpressModal = false;
      this.pagoExpress = { metodoPago: '', referencia: '' };
    },

    async confirmarPagoExpress() {
      if (!this.pagoExpress.metodoPago) {
        this.toast.warning('Seleccione un método de pago para continuar');
        return;
      }
      this.showPagoExpressModal = false;
      await this._procesarRegistroRecepcion();
    },

    // 🔥 PRINCIPAL: Procesa el registro de recepción y pago inicial en caja
    async _procesarRegistroRecepcion() {
      this.loading = true;
      try {
        if (this.form.accesorios && this.form.accesorios.length > 200) {
          this.form.accesorios = this.form.accesorios.slice(0, 200);
        }

        const montoCobrado = this.montoCobrarCalculado;
        const metodoUsado = montoCobrado > 0 ? (this.pagoInicial.metodoPago || this.pagoExpress.metodoPago || 'EFECTIVO_USD') : null;
        const refUsada = montoCobrado > 0 ? (this.pagoInicial.referencia || this.pagoExpress.referencia || '') : null;
        const costoEstimadoFinal = this.form.tipoDiagnostico === 'ADELANTADO'
          ? this.subtotalItemsAgregados
          : (Number(this.pagoInicial.costoEstimado || 0) || (this.form.tipoDiagnostico === 'EXPRESS' ? 10 : 0));
        
        const payload = {
          ...this.form,
          clavePatron: (this.form.accesorios || '').slice(0, 100),
          fechaIngreso: new Date(this.form.fechaIngreso).toLocaleString('sv-SE').replace(' ', 'T'),
          fechaReparacion: this.form.fechaReparacion ? new Date(this.form.fechaReparacion).toLocaleString('sv-SE').replace(' ', 'T') : null,
          fechaEntrega: this.form.fechaEntrega ? new Date(this.form.fechaEntrega).toLocaleString('sv-SE').replace(' ', 'T') : null,
          fechaPago: montoCobrado > 0 ? new Date().toISOString() : (this.form.fechaPago ? new Date(this.form.fechaPago).toLocaleString('sv-SE').replace(' ', 'T') : null),
          idTecnicoAsignado: this.form.idTecnicoAsignado !== 0 ? this.form.idTecnicoAsignado : null,
          idProductoServicios: this.form.idProductoServicios !== 0 ? this.form.idProductoServicios : null,
          idSede: this.form.idSede !== 'null' ? parseInt(this.form.idSede) : null,
          accesorios: this.form.accesorios || '',
          detalles: [],
          precioTotalUsd: costoEstimadoFinal,
          montoPagadoUsd: montoCobrado,
          servicioAcordado: this.itemsAgregados.length > 0 
            ? this.itemsAgregados.map(it => `${it.cantidad > 1 ? it.cantidad + 'x ' : ''}${it.nombre}`).join(', ') 
            : (this.pagoInicial.servicioNombre || null),
          metodoPago: metodoUsado,
          referenciaPago: refUsada,
          metodoPagoExpress: this.form.tipoDiagnostico === 'EXPRESS' ? metodoUsado : null,
          referenciaPagoExpress: this.form.tipoDiagnostico === 'EXPRESS' ? refUsada : null
        };

        console.log('📤 Enviando payload recepción:', payload);

        const res = await api.crearRecepcion(payload);

        if (res.success) {
          const recepcionCreada = res.data || payload;
          const idRecepcion = recepcionCreada.idRecepcionEquipo || recepcionCreada.IdRecepcionEquipo;
          const numeroOrdenGenerado = recepcionCreada.numeroOrden || recepcionCreada.NumeroOrden || this.numeroOrdenDisplay;

          let idDetallePrincipal = null;

          // Registrar cada ítem en RecepcionEquipoDetalle para que Fase 2 de SeguimientoOrden los cargue idénticamente
          if (this.form.tipoDiagnostico === 'ADELANTADO' && this.itemsAgregados.length > 0) {
            for (const item of this.itemsAgregados) {
              try {
                const isRepuesto = item.tipo === 'repuesto';
                const detRes = await api.crearDetalleRecepcion(idRecepcion, {
                  Cantidad: item.cantidad || 1,
                  PrecioUnitarioUsd: item.precio_unitario || item.precio || 0,
                  SubtotalUsd: item.precio_total || item.precio || 0,
                  PrecioUnitarioBs: +( (item.precio_unitario || item.precio || 0) * (this.tasaActual || 1) ).toFixed(2),
                  SubtotalBs: +( (item.precio_total || item.precio || 0) * (this.tasaActual || 1) ).toFixed(2),
                  CostoUnitarioUsd: item.costo || 0,
                  CostoTotalUsd: (item.costo || 0) * (item.cantidad || 1),
                  ComisionTecnicoUsd: item.comision || 0,
                  GarantiaDias: item.garantia || 0,
                  IdProducto: item.tipo === 'producto' ? item.id : null,
                  IdServicio: item.tipo === 'servicio' ? item.id : null,
                  IdTecnico: this.form.idTecnicoAsignado || null,
                  IdEstadoRecepcion: 1,
                  Observaciones: isRepuesto ? `REPUESTO: ${item.nombre}` : item.nombre
                });
                if (!idDetallePrincipal && detRes?.success && detRes.data) {
                  idDetallePrincipal = detRes.data.idRecepcionEquipoDetalle || detRes.data.IdRecepcionEquipoDetalle;
                }
              } catch (eDet) {
                console.warn('⚠️ Error al registrar detalle de ítem:', item.nombre, eDet);
              }
            }
          } else if (costoEstimadoFinal > 0 || this.pagoInicial.servicioNombre) {
            try {
              const detRes = await api.crearDetalleRecepcion(idRecepcion, {
                Cantidad: 1,
                PrecioUnitarioUsd: costoEstimadoFinal,
                SubtotalUsd: costoEstimadoFinal,
                PrecioUnitarioBs: +(costoEstimadoFinal * (this.tasaActual || 1)).toFixed(2),
                SubtotalBs: +(costoEstimadoFinal * (this.tasaActual || 1)).toFixed(2),
                IdTecnico: this.form.idTecnicoAsignado || null,
                IdEstadoRecepcion: 1,
                Observaciones: this.pagoInicial.servicioNombre ? `Servicio inicial: ${this.pagoInicial.servicioNombre}` : 'Recepción de equipo'
              });
              if (detRes?.success && detRes.data) {
                idDetallePrincipal = detRes.data.idRecepcionEquipoDetalle || detRes.data.IdRecepcionEquipoDetalle;
              }
            } catch (eDet) {
              console.warn('⚠️ Error registrando detalle único:', eDet);
            }
          }

          // Si hubo cobro inicial (montoCobrado > 0), registrar en SalidaEquipoDetalle y PagoSalidaEquipo para caja
          if (montoCobrado > 0) {
            try {
              const salidaRes = await crearSalidaEquipo({
                PrecioUnitarioUsd: montoCobrado,
                SubtotalUsd: montoCobrado,
                PrecioUnitarioBs: +(montoCobrado * (this.tasaActual || 1)).toFixed(2),
                SubtotalBs: +(montoCobrado * (this.tasaActual || 1)).toFixed(2),
                IdRecepcionEquipoDetalle: parseInt(idDetallePrincipal || idRecepcion),
                IdUsuario: this.form.idUsuario || 1,
                IdEstadoRecepcion: 1
              });

              const idSalida = salidaRes?.data?.idSalidaEquipoDetalle || salidaRes?.data?.IdSalidaEquipoDetalle;
              if (idSalida) {
                const tipoPagoNombre = (montoCobrado >= costoEstimadoFinal && costoEstimadoFinal > 0) ? 'Completo' : 'Abono';
                await crearPagoSalidaEquipo({
                  IdSalidaEquipoDetalle: idSalida,
                  NumeroRecibo: `REC-${numeroOrdenGenerado}`,
                  FechaPago: new Date().toISOString(),
                  MontoUsd: montoCobrado,
                  MontoBs: +(montoCobrado * (this.tasaActual || 1)).toFixed(2),
                  TasaBsUsd: this.tasaActual || 1,
                  IdFuenteTasa: this.idFuenteTasa || 1,
                  IdTasaVenta: this.idTasaVenta || 1,
                  TipoPago: tipoPagoNombre,
                  SaldoPendienteUsd: Math.max(0, costoEstimadoFinal - montoCobrado),
                  MetodoPago: metodoUsado || 'EFECTIVO_USD',
                  Referencia: refUsada || 'S/R',
                  IdUsuario: this.form.idUsuario || 1,
                  Estado: 'Completado',
                  Observaciones: `Pago Adelantado en Recepción (${tipoPagoNombre}: $${montoCobrado}) Orden #${numeroOrdenGenerado}`
                });
              }
            } catch (errPago) {
              console.error('⚠️ Error al registrar pago inicial en caja:', errPago);
            }
          }

          const metodoPagoLabel = this.metodosPago.find(m => m.value === metodoUsado)?.label || metodoUsado;
          if (montoCobrado > 0) {
            this.toast.success(`✅ Cobro de $${montoCobrado.toFixed(2)} registrado (${metodoPagoLabel}) — Orden #${numeroOrdenGenerado}`);
            notificarRecepcionExpress('Recepción con Pago creada', `Orden #${numeroOrdenGenerado}`, `Cliente: ${this.clienteSeleccionado?.nombre} · Pago: $${montoCobrado.toFixed(2)} (${metodoPagoLabel})`);
          } else {
            this.toast.success(`✅ Recepción #${numeroOrdenGenerado} registrada exitosamente`);
            notificarRecepcionExpress('Recepción creada', `Orden #${numeroOrdenGenerado}`, `Cliente: ${this.clienteSeleccionado?.nombre}`);
          }

          const tecnico = this.tecnicos.find(t => t.idUsuario === this.form.idTecnicoAsignado);
          const categoria = this.productoServicios.find(ps => ps.idProductoServicio === this.form.idProductoServicios);

          const ordenParaTicket = {
            ...recepcionCreada,
            equipo: categoria?.nombre || 'EQUIPO GENERICO',
            descripcion: this.form.descripcion,
            problema: this.form.problema,
            accesorios: this.form.accesorios,
            numeroOrden: numeroOrdenGenerado,
            fechaIngreso: payload.fechaIngreso,
            fechaReparacion: payload.fechaReparacion,
            fechaEntrega: payload.fechaEntrega,
            fechaPago: payload.fechaPago,
            montoDiagnostico: this.form.montoDiagnostico,
            tipoDiagnostico: this.form.tipoDiagnostico,
            servicioAcordado: payload.servicioAcordado,
            items: this.itemsAgregados,
            montoAcordado: costoEstimadoFinal,
            precioTotalUsd: costoEstimadoFinal,
            montoPagadoUsd: montoCobrado,
            metodoPago: metodoUsado,
            referenciaPago: refUsada,
            metodoPagoExpress: this.form.tipoDiagnostico === 'EXPRESS' ? metodoUsado : null
          };

          imprimirTicketDiagnostico(ordenParaTicket, this.clienteSeleccionado, tecnico);

          // Actualizar el número de orden en el frontend después de crear
          await this.generarNumeroOrdenDisplay();

          this.pagoExpress = { metodoPago: '', referencia: '' };
          this.resetForm();
        } else {
          this.toast.error(res.error || 'Error al registrar la recepción');
        }
      } catch (error) {
        console.error(error);
        this.toast.error('Error crítico al conectar con el servidor');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        ...this.form,
        idProductoServicios: 0,
        fechaIngreso: new Date().toLocaleString('sv-SE').replace(' ', 'T').slice(0, 16),
        fechaReparacion: '',
        fechaEntrega: '',
        fechaPago: '',
        descripcion: '',
        problema: '',
        accesorios: '',
        idCliente: 0,
        idTecnicoAsignado: 0,
        clavePatron: '',
        taller: '',
        idSede: null,
        tipoDiagnostico: 'GRATIS',
        montoDiagnostico: 0,
        actividadesRealizadas: ''
      };
      this.itemsAgregados = [];
      this.nombreItemManual = '';
      this.precioItemManual = 0;
      this.pagoInicial = {
        servicioNombre: '',
        costoEstimado: 0,
        modalidad: 'TOTAL',
        montoCobrar: 0,
        metodoPago: 'EFECTIVO_USD',
        referencia: ''
      };
      this.pagoExpress = { metodoPago: '', referencia: '' };
      this.clienteSeleccionado = null;
      this.productoSeleccionado = null;
      this.tecnicoSeleccionado = null;
      this.buscarProducto = '';
      this.buscarCliente = '';
      this.buscarTecnico = '';
      this.generarNumeroOrdenDisplay();
    },

    actualizarMontoDiagnostico() {
      if (this.form.tipoDiagnostico === 'EXPRESS') {
        this.form.montoDiagnostico = 10;
        this.cambiarModalidadPago('EXPRESS');
      } else if (this.form.tipoDiagnostico === 'ADELANTADO') {
        this.form.montoDiagnostico = 0;
        this.cambiarModalidadPago('TOTAL');
      } else {
        this.form.tipoDiagnostico = 'GRATIS';
        this.form.montoDiagnostico = 0;
        this.cambiarModalidadPago('NINGUNO');
        this.itemsAgregados = [];
      }
    },

    actualizarNombreTaller() {
      if (this.form.idSede === 'null' || this.form.idSede === null) {
        this.form.taller = '';
        return;
      }
      const sede = this.sedesList.find(s => s.idSede === parseInt(this.form.idSede));
      if (sede) {
        this.form.taller = sede.nombre;
        console.log("🏪 Taller actualizado a:", sede.nombre);
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

/* Forzar opciones legibles en modo oscuro */
.dark select option {
  background-color: #1f2937;
  color: #ffffff;
}
</style>